"use client";

import type { ReactElement } from "react";
import { Box, Text, Group, Stack } from "@mantine/core";
import {
  SEASON_DISTRIBUTION,
  PARENT_PALETTE,
  SEARCH_TREND,
  PRICING_INDEX,
  DEMOGRAPHICS,
  METHOD_PREFERENCE,
  MOTIVATIONS,
} from "./data";

/* ============================================================================
   Shared: LabeledDonut — SVG donut chart with external callout labels
   (leader lines from each slice to "Label · 24%" text outside the ring).
   ============================================================================ */
type DonutSlice = { label: string; percent: number; color: string };

function LabeledDonut({
  data,
  size = 640,
  innerRingRatio = 0.58,
  centerEyebrow,
  centerValue,
  centerLabel,
  labelFontSize = 12,
}: {
  data: DonutSlice[];
  size?: number;
  innerRingRatio?: number;
  centerEyebrow?: string;
  centerValue?: string;
  centerLabel?: string;
  labelFontSize?: number;
}) {
  const total = data.reduce((s, d) => s + d.percent, 0);
  // Wider canvas than tall — labels need horizontal room to extend outside the ring
  const w = size * 1.35;
  const h = size;
  const cx = w / 2;
  const cy = h / 2;
  const outerR = size * 0.26;
  const innerR = outerR * innerRingRatio;
  const labelR = outerR * 1.45;       // elbow of the leader (further out)
  const labelEdgeR = outerR * 1.06;   // where the leader exits the slice

  // Build slice paths
  let cursor = -Math.PI / 2; // start at top
  const slices = data.map((d) => {
    const sweep = (d.percent / total) * Math.PI * 2;
    const start = cursor;
    const end = cursor + sweep;
    cursor = end;
    const mid = (start + end) / 2;

    const x0 = cx + outerR * Math.cos(start);
    const y0 = cy + outerR * Math.sin(start);
    const x1 = cx + outerR * Math.cos(end);
    const y1 = cy + outerR * Math.sin(end);
    const xi0 = cx + innerR * Math.cos(end);
    const yi0 = cy + innerR * Math.sin(end);
    const xi1 = cx + innerR * Math.cos(start);
    const yi1 = cy + innerR * Math.sin(start);

    const largeArc = sweep > Math.PI ? 1 : 0;
    const path = `M${x0},${y0} A${outerR},${outerR} 0 ${largeArc} 1 ${x1},${y1} L${xi0},${yi0} A${innerR},${innerR} 0 ${largeArc} 0 ${xi1},${yi1} Z`;

    // Leader line endpoints
    const lx0 = cx + labelEdgeR * Math.cos(mid);
    const ly0 = cy + labelEdgeR * Math.sin(mid);
    const lx1 = cx + labelR * Math.cos(mid);
    const ly1 = cy + labelR * Math.sin(mid);
    const onRight = Math.cos(mid) >= 0;
    const lx2 = onRight ? lx1 + 12 : lx1 - 12;
    const ly2 = ly1;
    const textAnchor = onRight ? ("start" as const) : ("end" as const);
    const textX = onRight ? lx2 + 4 : lx2 - 4;

    return { d, path, mid, lx0, ly0, lx1, ly1, lx2, ly2, textAnchor, textX };
  });

  return (
    <Box style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: "auto", display: "block" }}>
        {/* Slices */}
        {slices.map((s, i) => (
          <path key={i} d={s.path} fill={s.d.color} stroke="white" strokeWidth={2} />
        ))}

        {/* Leader lines + labels */}
        {slices.map((s, i) => (
          <g key={`l${i}`}>
            <polyline
              points={`${s.lx0},${s.ly0} ${s.lx1},${s.ly1} ${s.lx2},${s.ly2}`}
              fill="none"
              stroke="#bdb5a8"
              strokeWidth={1}
            />
            <circle cx={s.lx0} cy={s.ly0} r={2} fill={s.d.color} />
            <text
              x={s.textX}
              y={s.ly2 + 4}
              fontSize={labelFontSize}
              fill="#1A1A2E"
              textAnchor={s.textAnchor}
              style={{ fontWeight: 500 }}
            >
              <tspan>{s.d.label}</tspan>
              <tspan fill="#A0522D" fontWeight={700} dx={6}>
                {s.d.percent}%
              </tspan>
            </text>
          </g>
        ))}

        {/* Center text */}
        {centerEyebrow && (
          <text
            x={cx}
            y={cy - 18}
            fontSize={11}
            fill="#888"
            textAnchor="middle"
            style={{ letterSpacing: "0.15em", textTransform: "uppercase" }}
          >
            {centerEyebrow}
          </text>
        )}
        {centerValue && (
          <text
            x={cx}
            y={cy + 14}
            fontSize={36}
            fill="#1A1A2E"
            textAnchor="middle"
            style={{ fontWeight: 600, letterSpacing: "-0.02em" }}
          >
            {centerValue}
          </text>
        )}
        {centerLabel && (
          <text x={cx} y={cy + 34} fontSize={12} fill="#666" textAnchor="middle">
            {centerLabel}
          </text>
        )}
      </svg>
    </Box>
  );
}

/* ============================================================================
   CHART 1 — SEASON DISTRIBUTION (donut with external labels, season colors)
   ============================================================================ */
export function SeasonDistributionChart({ height: _height = 540 }: { height?: number }) {
  // Order so parent-season families render contiguously around the ring:
  // Spring → Summer → Autumn → Winter clockwise.
  const ordered = [
    ...SEASON_DISTRIBUTION.filter((r) => r.parent === "Spring"),
    ...SEASON_DISTRIBUTION.filter((r) => r.parent === "Summer"),
    ...SEASON_DISTRIBUTION.filter((r) => r.parent === "Autumn"),
    ...SEASON_DISTRIBUTION.filter((r) => r.parent === "Winter"),
  ];
  const data: DonutSlice[] = ordered.map((r) => ({
    label: r.season,
    percent: r.percent,
    color: r.hex,
  }));
  const top = [...ordered].sort((a, b) => b.percent - a.percent)[0];

  return (
    <Box>
      <LabeledDonut
        data={data}
        size={680}
        innerRingRatio={0.62}
        centerEyebrow="Most common"
        centerValue={`${top.percent}%`}
        centerLabel={top.season}
        labelFontSize={13}
      />
      {/* Family legend (compact, reinforces the wedge groupings) */}
      <Group justify="center" gap="lg" mt={16}>
        {(["Spring", "Summer", "Autumn", "Winter"] as const).map((p) => {
          const pal = PARENT_PALETTE[p];
          return (
            <Group key={p} gap={6}>
              <Box w={10} h={10} style={{ borderRadius: "50%", background: pal.primary }} />
              <Text fz={11} c="dimmed" style={{ letterSpacing: "0.05em" }}>
                {pal.emoji} {p}
              </Text>
            </Group>
          );
        })}
      </Group>
    </Box>
  );
}

/* ============================================================================
   CHART 2 — SEARCH TREND
   Custom SVG area + line; warm gradient instead of generic violet.
   ============================================================================ */
export function SearchTrendChart({ height = 380 }: { height?: number }) {
  const w = 820;
  const h = height;
  const padLeft = 80;     // wider — room for "Search interest" Y-axis label
  const padRight = 24;
  const padTop = 56;      // wider top — room for peak callout to sit INSIDE chart bounds
  const padBottom = 56;   // wider bottom — room for x labels + axis caption
  const innerW = w - padLeft - padRight;
  const innerH = h - padTop - padBottom;
  const n = SEARCH_TREND.length;
  const stepX = innerW / (n - 1);

  const points = SEARCH_TREND.map((p, i) => {
    const x = padLeft + i * stepX;
    const y = padTop + innerH - (p.interest / 100) * innerH;
    return { x, y, ...p };
  });

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
  const areaPath = `${linePath} L${points[n - 1].x},${padTop + innerH} L${points[0].x},${padTop + innerH} Z`;

  const peak = points.reduce((a, b) => (b.interest > a.interest ? b : a));

  // Peak callout — sized to fit the text comfortably
  const peakBoxH = 26;
  const peakBoxW = 150;
  const peakBoxY = Math.max(padTop + 4, peak.y - 36);
  const peakBoxX = Math.min(
    Math.max(padLeft + 4, peak.x - peakBoxW / 2),
    padLeft + innerW - peakBoxW - 4
  );
  const connectorY = peakBoxY + peakBoxH;

  return (
    <Box style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: "auto", display: "block" }}>
        <defs>
          <linearGradient id="trendArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#A0522D" stopOpacity={0.32} />
            <stop offset="100%" stopColor="#FFE28A" stopOpacity={0.04} />
          </linearGradient>
          <linearGradient id="trendLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#A0522D" />
            <stop offset="100%" stopColor="#D2691E" />
          </linearGradient>
        </defs>

        {/* Y-axis caption (rotated) */}
        <text
          x={20}
          y={padTop + innerH / 2}
          fontSize="10"
          fill="#888"
          textAnchor="middle"
          transform={`rotate(-90 20 ${padTop + innerH / 2})`}
          style={{ letterSpacing: "0.15em", textTransform: "uppercase" }}
        >
          Search interest (0–100 index)
        </text>

        {/* gridlines + Y labels */}
        {[0, 25, 50, 75, 100].map((v) => {
          const y = padTop + innerH - (v / 100) * innerH;
          return (
            <g key={v}>
              <line
                x1={padLeft}
                y1={y}
                x2={padLeft + innerW}
                y2={y}
                stroke="#f0ece6"
                strokeWidth={1}
                strokeDasharray={v === 0 || v === 100 ? "0" : "2,3"}
              />
              <text x={padLeft - 10} y={y + 3} fontSize="10" fill="#999" textAnchor="end">
                {v}
              </text>
            </g>
          );
        })}

        {/* area + line */}
        <path d={areaPath} fill="url(#trendArea)" />
        <path d={linePath} fill="none" stroke="url(#trendLine)" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />

        {/* points */}
        {points.map((p) => (
          <circle
            key={p.month}
            cx={p.x}
            cy={p.y}
            r={p === peak ? 6 : 3.5}
            fill="#fff"
            stroke={p === peak ? "#A0522D" : "#C97B3F"}
            strokeWidth={p === peak ? 3 : 2}
          />
        ))}

        {/* peak callout — anchored INSIDE chart bounds with connector to point */}
        <line
          x1={peak.x}
          y1={connectorY}
          x2={peak.x}
          y2={peak.y - 8}
          stroke="#1A1A2E"
          strokeWidth={1}
          strokeDasharray="2,2"
        />
        <rect
          x={peakBoxX}
          y={peakBoxY}
          width={peakBoxW}
          height={peakBoxH}
          rx={13}
          fill="#1A1A2E"
        />
        <text
          x={peakBoxX + peakBoxW / 2}
          y={peakBoxY + 17}
          fontSize="11"
          fontWeight={600}
          fill="#fff"
          textAnchor="middle"
        >
          Peak · {peak.month} ({peak.interest})
        </text>

        {/* x labels */}
        {points.map((p, i) => (
          <text
            key={p.month}
            x={p.x}
            y={padTop + innerH + 18}
            fontSize="10"
            fill="#888"
            textAnchor="middle"
            style={{ display: i % 2 === 0 || i === n - 1 ? "block" : "none" }}
          >
            {p.month}
          </text>
        ))}

        {/* X-axis caption */}
        <text
          x={padLeft + innerW / 2}
          y={h - 14}
          fontSize="10"
          fill="#888"
          textAnchor="middle"
          style={{ letterSpacing: "0.15em", textTransform: "uppercase" }}
        >
          Month (Jun '25 — May '26)
        </text>
      </svg>

      {/* Explainer caption — answers "wtf is 100?" */}
      <Text fz={11} c="dimmed" mt={8} style={{ lineHeight: 1.5, fontStyle: "italic" }}>
        Google Trends index — 100 marks the highest-interest month in the period (March 2026).
        50 means half as much search volume as that peak. Not a count of searches.
      </Text>
    </Box>
  );
}

/* ============================================================================
   CHART 3 — PRICING INDEX
   Stacked range bars (low → high) with avg marker. Warm earthy palette.
   ============================================================================ */
export function PricingChart({ height: _height = 380 }: { height?: number }) {
  const maxPrice = Math.max(...PRICING_INDEX.map((p) => p.high));
  const tiers = [
    { color: "#FBCEB1", label: "AI" },
    { color: "#C9B6D4", label: "Virtual" },
    { color: "#C4A57A", label: "In-person small" },
    { color: "#A0522D", label: "In-person metro" },
    { color: "#1A1A2E", label: "Certified HoC" },
  ];

  return (
    <Stack gap={18}>
      {PRICING_INDEX.map((row, i) => {
        const tier = tiers[i];
        const lowPct = (row.low / maxPrice) * 100;
        const avgPct = (row.avg / maxPrice) * 100;
        const highPct = (row.high / maxPrice) * 100;
        return (
          <Box key={row.service}>
            <Group justify="space-between" mb={6}>
              <Text fz={13} fw={500}>{row.service}</Text>
              <Group gap={12}>
                <Text fz={11} c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                  ${row.low}–${row.high}
                </Text>
                <Text fz={13} fw={700} style={{ color: tier.color === "#FBCEB1" ? "#7A4A00" : tier.color, fontVariantNumeric: "tabular-nums" }}>
                  avg ${row.avg}
                </Text>
              </Group>
            </Group>
            <Box
              style={{
                position: "relative",
                height: 22,
                background: "#fafafa",
                borderRadius: 11,
                overflow: "hidden",
              }}
            >
              {/* full range bar */}
              <Box
                style={{
                  position: "absolute",
                  left: `${lowPct}%`,
                  width: `${highPct - lowPct}%`,
                  top: 0,
                  bottom: 0,
                  background: `linear-gradient(90deg, ${tier.color}55 0%, ${tier.color} 100%)`,
                  borderRadius: 11,
                }}
              />
              {/* avg marker */}
              <Box
                style={{
                  position: "absolute",
                  left: `calc(${avgPct}% - 2px)`,
                  top: -2,
                  bottom: -2,
                  width: 4,
                  background: "#1A1A2E",
                  borderRadius: 2,
                  boxShadow: "0 0 0 2px white",
                }}
              />
            </Box>
          </Box>
        );
      })}
      <Group gap={16} mt={4}>
        <Group gap={6}>
          <Box w={12} h={3} style={{ background: "#1A1A2E", borderRadius: 2 }} />
          <Text fz={10} c="dimmed">Average</Text>
        </Group>
        <Group gap={6}>
          <Box w={16} h={6} style={{ background: "linear-gradient(90deg,#C4A57A55,#C4A57A)", borderRadius: 3 }} />
          <Text fz={10} c="dimmed">Low → High range</Text>
        </Group>
      </Group>
    </Stack>
  );
}

/* ============================================================================
   CHART 4 — METHOD PREFERENCE (donut with external callout labels)
   ============================================================================ */
export function MethodPreferenceChart({ height: _height = 420 }: { height?: number }) {
  const colors = ["#A0522D", "#1A1A2E", "#C9B6D4", "#d4d4d4"];
  const data: DonutSlice[] = METHOD_PREFERENCE.map((d, i) => ({
    label: d.method,
    percent: d.percent,
    color: colors[i],
  }));
  const top = [...data].sort((a, b) => b.percent - a.percent)[0];

  return (
    <LabeledDonut
      data={data}
      size={620}
      innerRingRatio={0.62}
      centerEyebrow="Most popular"
      centerValue={`${top.percent}%`}
      centerLabel={top.label}
      labelFontSize={14}
    />
  );
}

/* ============================================================================
   CHART 5 — DEMOGRAPHICS (donut with external callout labels)
   ============================================================================ */
export function DemographicsChart({ height: _height = 440 }: { height?: number }) {
  const palette = ["#FBCEB1", "#F88379", "#C97B3F", "#A0522D", "#6B3410", "#C9B6D4", "#6F2DA8", "#A39FAE"];
  const data: DonutSlice[] = DEMOGRAPHICS.map((d, i) => ({
    label: d.label,
    percent: d.percent,
    color: palette[i % palette.length],
  }));
  const top = [...data].sort((a, b) => b.percent - a.percent)[0];

  return (
    <LabeledDonut
      data={data}
      size={680}
      innerRingRatio={0.6}
      centerEyebrow="Largest cohort"
      centerValue={`${top.percent}%`}
      centerLabel={top.label}
      labelFontSize={13}
    />
  );
}

/* ============================================================================
   CHART 6 — MOTIVATIONS
   Editorial horizontal bars with warm gradient.
   ============================================================================ */
export function MotivationsChart({ height: _height = 360 }: { height?: number }) {
  const max = Math.max(...MOTIVATIONS.map((m) => m.percent));
  const palette = ["#A0522D", "#C97B3F", "#C4A57A", "#FBCEB1", "#C9B6D4", "#1A1A2E"];

  return (
    <Stack gap={14}>
      {MOTIVATIONS.map((row, i) => {
        const widthPct = (row.percent / max) * 100;
        const color = palette[i];
        return (
          <Box key={row.reason}>
            <Group justify="space-between" mb={6}>
              <Text fz={13} fw={500}>{row.reason}</Text>
              <Text fz={13} fw={700} style={{ color, fontVariantNumeric: "tabular-nums" }}>
                {row.percent}%
              </Text>
            </Group>
            <Box
              style={{
                position: "relative",
                height: 14,
                background: "#fafafa",
                borderRadius: 7,
                overflow: "hidden",
              }}
            >
              <Box
                style={{
                  position: "absolute",
                  inset: 0,
                  width: `${widthPct}%`,
                  background: `linear-gradient(90deg, ${color}cc 0%, ${color} 100%)`,
                  borderRadius: 7,
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

export const CHART_BY_ID: Record<string, (props: { height?: number }) => ReactElement> = {
  "season-distribution": SeasonDistributionChart,
  "search-trend": SearchTrendChart,
  "pricing-index": PricingChart,
  demographics: DemographicsChart,
  "method-preference": MethodPreferenceChart,
  motivations: MotivationsChart,
};
